import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { useMacros, type Macro } from '@/hooks/useMacros';
import { useUserPermissions } from '@/hooks/useUserPermissions';
import { Sparkles, Info } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface MacroDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  macro?: Macro | null;
  mode: 'create' | 'edit';
}

const AVAILABLE_VARIABLES = [
  { key: 'nome_cliente', description: 'Nome do cliente' },
  { key: 'telefone', description: 'Telefone do cliente' },
  { key: 'email', description: 'Email do cliente' },
  { key: 'protocolo', description: 'Número do protocolo' },
  { key: 'operador', description: 'Nome do operador' },
];

export function MacroDialog({ open, onOpenChange, macro, mode }: MacroDialogProps) {
  const { isAdmin } = useUserPermissions();
  const { createMacro, updateMacro, isCreating, isUpdating } = useMacros();
  
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [isGlobal, setIsGlobal] = useState(false);

  // Reset form when dialog opens/closes or macro changes
  useEffect(() => {
    if (open) {
      if (mode === 'edit' && macro) {
        setTitle(macro.title);
        setContent(macro.content);
        setIsGlobal(macro.is_global);
      } else {
        setTitle('');
        setContent('');
        setIsGlobal(false);
      }
    }
  }, [open, mode, macro]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim() || !content.trim()) {
      return;
    }

    if (mode === 'create') {
      createMacro(
        { title, content, is_global: isGlobal },
        {
          onSuccess: () => {
            onOpenChange(false);
          },
        }
      );
    } else if (mode === 'edit' && macro) {
      updateMacro(
        { id: macro.id, title, content },
        {
          onSuccess: () => {
            onOpenChange(false);
          },
        }
      );
    }
  };

  const insertVariable = (variable: string) => {
    const newContent = content + `{{${variable}}}`;
    setContent(newContent);
  };

  const isLoading = isCreating || isUpdating;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              {mode === 'create' ? 'Criar Nova Macro' : 'Editar Macro'}
            </DialogTitle>
            <DialogDescription>
              {mode === 'create'
                ? 'Crie uma mensagem pronta para uso rápido no atendimento'
                : 'Edite a mensagem pronta'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Título */}
            <div className="space-y-2">
              <Label htmlFor="title">
                Título da Macro <span className="text-red-500">*</span>
              </Label>
              <Input
                id="title"
                placeholder="Ex: Saudação inicial"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                disabled={isLoading}
              />
              <p className="text-xs text-muted-foreground">
                Nome curto para identificar a macro
              </p>
            </div>

            {/* Conteúdo */}
            <div className="space-y-2">
              <Label htmlFor="content">
                Mensagem <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="content"
                placeholder="Olá {{nome_cliente}}, tudo bem?"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                required
                disabled={isLoading}
                rows={6}
                className="resize-none"
              />
              <p className="text-xs text-muted-foreground">
                Use variáveis para personalizar a mensagem
              </p>
            </div>

            {/* Variáveis disponíveis */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Variáveis Disponíveis</Label>
              <div className="flex flex-wrap gap-2">
                {AVAILABLE_VARIABLES.map((variable) => (
                  <Badge
                    key={variable.key}
                    variant="outline"
                    className="cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors"
                    onClick={() => insertVariable(variable.key)}
                    title={`Clique para inserir: ${variable.description}`}
                  >
                    {`{{${variable.key}}}`}
                  </Badge>
                ))}
              </div>
              <p className="text-xs text-muted-foreground flex items-start gap-1">
                <Info className="h-3 w-3 mt-0.5 flex-shrink-0" />
                Clique em uma variável para inserir no texto
              </p>
            </div>

            {/* Global/Individual - apenas para criação e se for admin */}
            {mode === 'create' && isAdmin && (
              <Alert>
                <AlertDescription className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="is-global" className="text-sm font-medium cursor-pointer">
                      Macro Global (Toda Organização)
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Se ativado, todos os usuários poderão usar esta macro
                    </p>
                  </div>
                  <Switch
                    id="is-global"
                    checked={isGlobal}
                    onCheckedChange={setIsGlobal}
                    disabled={isLoading}
                  />
                </AlertDescription>
              </Alert>
            )}

            {mode === 'edit' && macro?.is_global && (
              <Alert>
                <AlertDescription className="text-xs">
                  Esta é uma macro global. Qualquer alteração será visível para todos os usuários da organização.
                </AlertDescription>
              </Alert>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isLoading || !title.trim() || !content.trim()}>
              {isLoading ? 'Salvando...' : mode === 'create' ? 'Criar Macro' : 'Salvar Alterações'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
